// Service hook (TS-2.5.01, Resource Accessor layer) - reads one project (GET
// /api/projects/{key}) for the detail/edit screen. `projectKey` of `null` means "create" (no GET
// is issued; the caller renders an empty ProjectRecord instead) - kept as one hook rather than two
// so the detail page's dirty-guard/save-flow logic does not fork on create vs. edit.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import { EntityTypes } from "../../../api/generated/contracts";
import type { ProjectDetail } from "../../../api/generated/contracts";
import { portfolioDataApi } from "../../../api/portfolioData";
import { useInvalidation } from "../../../realtime/useInvalidation";

export interface UseProjectDetailResult {
  readonly detail: ProjectDetail | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
  /** Lets the save flow apply a fresh ProjectDetail from a SaveResult directly, without a second
   *  round trip - the same value a refetch would eventually converge on. */
  readonly setDetail: (next: ProjectDetail) => void;
}

export function useProjectDetail(projectKey: string | null): UseProjectDetailResult {
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(projectKey !== null);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (projectKey === null) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    void portfolioDataApi
      .getProject(projectKey)
      .then((next) => {
        setDetail(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The project could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [projectKey]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, EntityTypes.Project);

  return { detail, loading, error, refresh, setDetail };
}
